import { DELETE_TENANT_WORD, type Tenant } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { navigate } from '../lib/router';
import { Button, ErrorText, Field } from '../ui';
import { CompanyList, InizioPage } from './sinapsi/Inizio';
import { useTenants } from './TopBar';

/**
 * «Aggiungi» e le aziende (contratto n. 1 versione 2.0, art. 1-2; ADR 0171): la pagina nello stile Sinapsi con la sola
 * partita IVA e i testi del contratto; sotto, le aziende già aggiunte. Vale sia come ingresso di chi non ha ancora
 * un'azienda sia da Impostazioni › Aziende.
 */
export function TenantsPage({ riparti = false }: { riparti?: boolean }) {
  const queryClient = useQueryClient();
  const tenants = useTenants();
  const [deleting, setDeleting] = useState<Tenant | null>(null);
  const active = (tenants.data ?? []).filter((t) => t.status === 'active');
  return (
    <>
      <InizioPage riparti={riparti} onExit={active.length ? { label: 'Entra nell’azienda', go: () => navigate(`/t/${active[0]!.id}`) } : undefined}>
        <CompanyList onDelete={(t) => setDeleting(t)} />
      </InizioPage>
      {deleting && (
        <DeleteTenantDialog
          tenant={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            void queryClient.invalidateQueries({ queryKey: ['tenants'] });
          }}
        />
      )}
    </>
  );
}

/** Conferma dell'eliminazione definitiva: avviso di irreversibilità e parola da scrivere. */
function DeleteTenantDialog({
  tenant,
  onClose,
  onDeleted,
}: {
  tenant: Tenant;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [word, setWord] = useState('');
  const remove = useMutation({
    mutationFn: () => api<void>(`/tenants/${tenant.id}/delete`, { body: { confirm_word: word } }),
    onSuccess: onDeleted,
  });
  const close = () => {
    if (!remove.isPending) onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={close}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-tenant-title"
        className="w-full max-w-md space-y-4 rounded-xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          remove.mutate();
        }}
      >
        <h2 id="delete-tenant-title" className="text-lg font-semibold text-red-800">
          Eliminare «{tenant.name}»?
        </h2>
        <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          <p className="font-semibold">Questa azione è irreversibile.</p>
          <p>
            L’azienda verrà eliminata per sempre con il suo database, i documenti caricati e tutto
            ciò che contiene: processi, persone, archivio, collegamenti. Non potrà essere
            recuperata.
          </p>
        </div>
        <Field
          label={`Per confermare scrivi ${DELETE_TENANT_WORD}`}
          value={word}
          onChange={(e) => setWord(e.target.value)}
          autoFocus
          autoComplete="off"
          required
        />
        <ErrorText>{remove.error ? errorMessage(remove.error) : ''}</ErrorText>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={close} disabled={remove.isPending}>
            Annulla
          </Button>
          <Button
            type="submit"
            className="bg-red-700! hover:bg-red-800! disabled:bg-red-700/40!"
            disabled={remove.isPending || word.trim().toUpperCase() !== DELETE_TENANT_WORD}
          >
            {remove.isPending ? 'Eliminazione…' : 'Elimina per sempre'}
          </Button>
        </div>
      </form>
    </div>
  );
}
